import { Box } from "@chakra-ui/react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ColorIcon, customEvent, HackleEventName, Spacing } from "goi_common";
import { BaseButton, BaseStack, baseColors } from "goi_common/src/design-system";

import type { CostConfig, GuestCostRange, GuestRangeKey } from "../utils/parlorIntroCost";
import CompositionCard from "./CompositionCard";
import StickyCostCard from "./StickyCostCard";
import QuestionBottomCta, { QUESTION_BOTTOM_CTA_SPACE_VERTICAL } from "./QuestionBottomCta";
import QuestionTopBar from "./QuestionTopBar";
import UrgentCallButton from "./UrgentCallButton";

/** 선택지. value 는 기존 견적 body(condolers)의 값 그대로 */
export const GUEST_OPTIONS: {
  value: GuestRangeKey;
  label: string;
  sub: string;
  event: "e5_g_family" | "e5_g_50" | "e5_g_100" | "e5_g_200" | "e5_g_unknown";
}[] = [
  { value: "0명(무빈소)", label: "조문객 없이", sub: "", event: "e5_g_family" },
  { value: "50명 내외", label: "50명", sub: "안팎", event: "e5_g_50" },
  { value: "100명 내외", label: "100명", sub: "안팎", event: "e5_g_100" },
  { value: "200명 이상", label: "200명", sub: "이상", event: "e5_g_200" },
  // 다시 넣음(2026-11-05 주완): 다음 화면에서 조문객 수를 바꿀 수 없어서. 비용은 100명 기준으로 보여 준다
  { value: "모르겠어요", label: "잘 모르겠어요", sub: "", event: "e5_g_unknown" },
];

/**
 * E5 조문객 질문(③) → 「이 범위는 왜 생길까요」(④)를 한 화면 안에서 이어 보여 준다.
 * 질문 · 선택지가 사라지고 위 비용 카드는 그대로 남아, 아래로 항목별 막대가 차례로 펼쳐진다.
 * 새 페이지로 넘어가는 느낌이 아니라 「방금 본 숫자를 펼쳐 보여 준다」 가 목표(2026-11-04 주완).
 * 질문이 하나뿐이라 진행 막대는 두지 않고 뒤로 가기만 둔다.
 */
export default function GuestCountStep({
  range,
  selected,
  onSelect,
  phase,
  config,
  onNext,
  onGuest,
  onBinso,
  onAltar,
  onFood,
  onExplore,
  onBack,
  onUrgent,
}: {
  range: GuestCostRange | null;
  selected: GuestRangeKey | null;
  onSelect: (value: GuestRangeKey) => void;
  /** question: 조문객 질문, breakdown: 무엇에 따라 달라지는지 */
  phase: "question" | "breakdown";
  /** breakdown: 지금 설정(조문객 · 빈소)의 예상 비용과 항목 */
  config: CostConfig | null;
  onNext: (value: GuestRangeKey) => void;
  /** 비용 구성에서 조문객 수를 바꿨을 때 */
  onGuest: (value: GuestRangeKey) => void;
  /** 비용 구성에서 빈소 평형을 바꿨을 때 */
  onBinso: (pyeong: number) => void;
  /** 비용 구성에서 제단꽃 등급을 바꿨을 때(0 = 기본) */
  onAltar: (index: number) => void;
  /** 비용 구성에서 식사 인분을 바꿨을 때 */
  onFood: (servings: number) => void;
  /** 비용 구성 아래 「우리 가족 맞춤 견적 받기」. 누르면 열리는 시트 안에서 바로 받는 방법(카카오톡 / 없이)을 골라 약속이 바로 이어진다 */
  onExplore: () => void;
  onBack: () => void;
  onUrgent: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const isBreakdown = phase === "breakdown";
  const shown = selected && range ? range.byGuest[selected] : null;

  return (
    <BaseStack width="100%" bg={baseColors.background_normal}>
      <QuestionTopBar onBack={onBack} />
      {range && !isBreakdown && <StickyCostCard range={range} selected={shown} />}
      {range && isBreakdown && shown && selected && config && (
        <CompositionCard
          config={config}
          guest={selected}
          reduceMotion={!!reduceMotion}
          onGuest={onGuest}
          onBinso={onBinso}
          onAltar={onAltar}
          onFood={onFood}
          // 지금 E5 는 첫 화면 검증만. 직접 맞춰 보기(small hook)는 나중 실험용으로 꺼 둔다(2026-11-05 주완)
          adjustable={false}
          startSegment={{
            left:
              ((shown.lo - range.minManwon) / Math.max(range.maxManwon - range.minManwon, 1)) * 100,
            width: ((shown.hi - shown.lo) / Math.max(range.maxManwon - range.minManwon, 1)) * 100,
          }}
        />
      )}

      <BaseStack width="100%" px="20px">
        <AnimatePresence initial={false} exitBeforeEnter>
          {!isBreakdown ? (
            <motion.div
              key="question"
              // 뒤로 돌아올 때는 움직임 없이 바로 보인다(주완: 이상하게 떠 보여서)
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              // 넘어갈 때는 바로 사라진다. 사라지는 동안 위에서 커지는 비용 카드 아래에 질문이 잠깐 남아 보였다(2026-11-04 주완)
              exit={{ opacity: 0, transition: { duration: 0 } }}
            >
              <Spacing gap={12} />
              <BaseStack direction="horizontal" width="100%" alignItems="center">
                <Box flex={1}>
                  <Box
                    as="h2"
                    className="title_3_b"
                    color={baseColors.label_normal}
                    wordBreak="keep-all"
                  >
                    조문객이 몇 분 정도
                    <br />
                    오시나요?
                  </Box>
                  <Spacing gap={6} />
                  <Box className="body_2_normal_r" color={baseColors.label_assistive_1}>
                    정확하지 않아도 괜찮아요
                  </Box>
                </Box>
                {/* 상주 두 명(완장 · 흰 리본). 조문객을 맞는 쪽이라 지도사보다 질문에 맞다(주완) */}
                <BaseStack direction="horizontal" alignItems="flex-end" flexShrink={0}>
                  <ColorIcon name="icon_Graphic_MournerFemale" size={48} />
                  {/* 두 그림을 살짝 겹친다(여백 대신 위치 이동) */}
                  <Box transform="translateX(-8px)" display="flex">
                    <ColorIcon name="icon_Graphic_MournerMale" size={48} />
                  </Box>
                </BaseStack>
              </BaseStack>

              <Spacing gap={20} />
              <BaseStack width="100%" spacing={8}>
                {GUEST_OPTIONS.map((o) => {
                  const on = selected === o.value;
                  return (
                    <Box
                      key={o.value}
                      as="button"
                      type="button"
                      width="100%"
                      height="52px"
                      px="16px"
                      display="flex"
                      alignItems="center"
                      borderRadius="6px"
                      bg={on ? baseColors.orange_100 : baseColors.fill_normal}
                      cursor="pointer"
                      onClick={() => {
                        // 기존 폼과 같은 조문객 선택 이벤트 · Hackle 지표(A 와 같은 이름으로 비교)
                        customEvent({
                          newGtm: {
                            path: "parlor_match_request",
                            trigger_and_target: "condolers-answer",
                          },
                        });
                        customEvent({
                          hackle: {
                            key: HackleEventName["견적 요청 - 조문객 수 선택"],
                            properties: { name: String(o.value) },
                          },
                        });
                        onSelect(o.value);
                      }}
                    >
                      <Box
                        className={on ? "body_1_normal_sb" : "body_1_normal_m"}
                        color={on ? baseColors.primary_orange : baseColors.label_normal}
                      >
                        {o.label}
                      </Box>
                      {o.sub && (
                        <>
                          <Spacing gap={6} orientation="horizontal" />
                          <Box className="label_1_r" color={baseColors.label_assistive_1}>
                            {o.sub}
                          </Box>
                        </>
                      )}
                    </Box>
                  );
                })}
              </BaseStack>
            </motion.div>
          ) : (
            <motion.div key="breakdown" />
          )}
        </AnimatePresence>
      </BaseStack>

      <Spacing gap={QUESTION_BOTTOM_CTA_SPACE_VERTICAL} />
      <QuestionBottomCta>
        {isBreakdown ? (
          <BaseButton type="primary" size="large" onClick={onExplore}>
            <Box className="body_1_normal_m">우리 가족 맞춤 견적 받기</Box>
          </BaseButton>
        ) : (
          <BaseButton
            type="primary"
            size="large"
            disabled={selected === null}
            onClick={() => selected && onNext(selected)}
          >
            <Box className="body_1_normal_m">다음</Box>
          </BaseButton>
        )}
        <Spacing gap={8} />
        <UrgentCallButton onClick={onUrgent} />
      </QuestionBottomCta>
    </BaseStack>
  );
}
